import { z } from "zod";

import type { FunctionComponent, KeyboardEvent } from "react";

import { classNames } from "@/utils/classNames";

import { TrackerSignalResultAdditionalSchema } from "../../../../Apollo/schema/get-people";

import { SignalActionBar, SignalActionContext } from "./SignalActionBar";
import { SignalResultContent } from "./SignalResultContent";

type TrackerSignalResultAdditional = z.infer<typeof TrackerSignalResultAdditionalSchema>;

interface SignalCardProps {
	signal: TrackerSignalResultAdditional;
	inboxIndex: number;
	prospectName: string;
	organizationName: string;
	currentLeadIndex: number;
	cardKey: string;
	isSummaryExpanded: boolean;
	onSelectLead: (inboxIndex: number) => void;
	onToggleSummary: (signalId: number) => void;
	onReachOut?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	onGenerateDrafts?: (inboxIndex: number, signal: TrackerSignalResultAdditional) => void;
	setCardRef: (key: string) => (el: HTMLDivElement | null) => void;
	actionContext?: SignalActionContext;
}

export const SignalCard: FunctionComponent<SignalCardProps> = ({
	signal,
	inboxIndex,
	prospectName,
	organizationName,
	currentLeadIndex,
	cardKey,
	isSummaryExpanded,
	onSelectLead,
	onToggleSummary,
	onReachOut,
	onGenerateDrafts,
	setCardRef,
	actionContext,
}) => {
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) {
			return;
		}

		event.preventDefault();
		onSelectLead(inboxIndex);
	};

	return (
		<div
			ref={setCardRef(cardKey)}
			aria-label={`Select lead ${prospectName}`}
			className={classNames(
				"flex w-full max-w-full flex-col border rounded-lg overflow-hidden bg-white p-4 min-h-fit transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-action focus:ring-offset-2",
				inboxIndex === currentLeadIndex ? "border-blue-action shadow-sm" : "border-border-light"
			)}
			data-inbox-index={inboxIndex}
			role="button"
			tabIndex={0}
			onClick={() => onSelectLead(inboxIndex)}
			onKeyDown={handleKeyDown}
		>
			<div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-border-light">
				<div className="flex items-center gap-2 truncate">
					<span className="text-xs font-semibold text-text truncate">{prospectName}</span>
					{organizationName && (
						<span className="text-xs text-text-meta truncate">· {organizationName}</span>
					)}
				</div>
			</div>

			<SignalResultContent
				isSummaryExpanded={isSummaryExpanded}
				signal={signal}
				stopPropagationOnActions={true}
				onToggleSummary={onToggleSummary}
			/>

			<SignalActionBar
				context={actionContext}
				prospectName={prospectName}
				signal={signal}
				onGenerateDrafts={onGenerateDrafts ? () => onGenerateDrafts(inboxIndex, signal) : undefined}
				onReachOut={onReachOut ? () => onReachOut(inboxIndex, signal) : undefined}
			/>
		</div>
	);
};
